import { useEffect, useState } from "react";
import {
  fetchMidpointMediaDigitalOcean,
  fetchMidpointMediaHealth,
  fetchMidpointMediaHealthHistory,
} from "../../../api/midpointMediaAdminApi";
import MidpointMediaSetupPanel from "../../../components/midpointMedia/admin/MidpointMediaSetupPanel";
import {
  ClientPortalError,
  ClientPortalLoading,
} from "../../../components/clientPortal/ClientPortalStates";
import useMidpointMediaAdminView from "../../../hooks/useMidpointMediaAdminView";
import "../../../styles/midpointMediaAdmin.css";

export default function MidpointMediaAdminInfrastructurePage() {
  const { clientViewId } = useMidpointMediaAdminView();
  const [state, setState] = useState({ loading: true, error: "", health: null, droplet: null, history: null });

  useEffect(() => {
    if (!clientViewId) return undefined;
    let cancelled = false;
    Promise.allSettled([
      fetchMidpointMediaHealth(clientViewId),
      fetchMidpointMediaHealthHistory(clientViewId, "24h"),
      fetchMidpointMediaDigitalOcean(clientViewId),
    ]).then(([health, history, droplet]) => {
      if (cancelled) return;
      setState({
        loading: false,
        error:
          health.status === "rejected"
            ? health.reason?.message || "Unable to load application health."
            : "",
        health: health.status === "fulfilled" ? health.value : null,
        history: history.status === "fulfilled" ? history.value : null,
        droplet: droplet.status === "fulfilled" ? droplet.value : null,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [clientViewId]);

  if (!clientViewId) return <p className="empty-state">No workspace assigned.</p>;
  if (state.loading) return <ClientPortalLoading message="Loading infrastructure…" />;
  if (state.error && !state.health) return <ClientPortalError message={state.error} />;

  const summary = state.health?.summary || {};
  const droplet = state.droplet?.live ? state.droplet.droplet : state.droplet?.demo?.droplet;
  const metrics = state.droplet?.demo?.metrics;

  return (
    <div className="mm-admin-page">
      <header>
        <p className="mm-admin-kicker">Infrastructure</p>
        <h1>Server health</h1>
        <p className="mm-admin-lead">
          Live signals from the Midpoint API process, plus DigitalOcean droplet status when an API
          token is configured.
        </p>
      </header>

      <section className="mm-admin-grid">
        <article className="mm-admin-card">
          <span className="mm-admin-badge mm-admin-badge--live">API process</span>
          <h3>Status</h3>
          <div className="mm-admin-kpi">
            {summary.status || "—"}
            <span>{summary.hostname || "this host"}</span>
          </div>
        </article>
        <article className="mm-admin-card">
          <h3>Uptime</h3>
          <div className="mm-admin-kpi">
            {summary.uptimeLabel || "—"}
            <span>Since last process start</span>
          </div>
        </article>
        <article className="mm-admin-card">
          <h3>Memory</h3>
          <div className="mm-admin-kpi">
            {summary.rssMb != null ? `${summary.rssMb} MB` : "—"}
            <span>RSS · heap {summary.heapUsedMb != null ? `${summary.heapUsedMb} MB` : "—"}</span>
          </div>
        </article>
        <article className="mm-admin-card">
          <h3>Database</h3>
          <div className="mm-admin-kpi">
            {summary.databaseOk ? "ok" : "down"}
            <span>
              {summary.databaseLatencyMs != null ? `${summary.databaseLatencyMs} ms` : "latency"}
            </span>
          </div>
        </article>
      </section>

      <section className="mm-admin-card">
        <h2>DigitalOcean droplet</h2>
        {state.droplet?.live ? (
          <span className="mm-admin-badge mm-admin-badge--live">Live from DigitalOcean API</span>
        ) : (
          <span className="mm-admin-badge mm-admin-badge--demo">Demo until a token is set</span>
        )}
        {droplet ? (
          <table className="mm-admin-table">
            <tbody>
              <tr>
                <th>Name</th>
                <td>{droplet.name}</td>
              </tr>
              <tr>
                <th>Region</th>
                <td>{droplet.region || "—"}</td>
              </tr>
              <tr>
                <th>Status</th>
                <td>{droplet.status}</td>
              </tr>
              <tr>
                <th>Size</th>
                <td>
                  {droplet.vcpus ? `${droplet.vcpus} vCPU · ` : ""}
                  {droplet.memoryMb ? `${droplet.memoryMb} MB · ` : ""}
                  {droplet.diskGb ? `${droplet.diskGb} GB disk` : ""}
                </td>
              </tr>
              <tr>
                <th>Public IP</th>
                <td>{droplet.ip || "—"}</td>
              </tr>
              {metrics && !state.droplet?.live ? (
                <tr>
                  <th>Sample load (demo)</th>
                  <td>
                    CPU {metrics.cpuPercent}% · RAM {metrics.memoryPercent}% · Disk{" "}
                    {metrics.diskPercent}%
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        ) : null}
      </section>

      {!state.droplet?.live ? (
        <MidpointMediaSetupPanel
          instructions={state.droplet?.instructions}
          error={state.droplet?.error}
        />
      ) : null}

      {state.history?.history?.empty === false ? (
        <p className="muted">
          {state.history.history.pointCount} health samples in the last{" "}
          {state.history.history.rangeLabel || "24h"}.
        </p>
      ) : (
        <p className="muted">History fills in after the in-process sampler has run for a few minutes.</p>
      )}
    </div>
  );
}
